import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { chatAPI } from '@/api/chat';
import { showToast } from '@/utils/toast';

/**
 * Chat Context
 * Manages chat sessions, messages, and favorites
 */
const ChatContext = createContext(null);

/**
 * ChatProvider component
 * Wraps the application to provide chat state and functions
 */
export function ChatProvider({ children }) {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [favorites, setFavorites] = useState([]);
  
  /**
   * Load all sessions for a user
   * @param {string} userId - User ID
   * @param {string} source - Filter by source ("chat" or "study")
   */
  const loadSessions = useCallback(async (userId, source = 'chat') => {
    try {
      const data = await chatAPI.getSessions(userId, source);
      setSessions(data);
      
      if (data.length > 0 && !currentSession) {
        // Load most recently updated session (first in array, already sorted by backend)
        const mostRecentSession = data[0];
        console.log('Loading most recent session:', mostRecentSession.title);
        setCurrentSession(mostRecentSession);
        await loadMessages(mostRecentSession.id);
      } else if (data.length === 0) {
        // No sessions - create one automatically
        console.log('No sessions found, creating new one...');
        const newSession = await chatAPI.createSession(userId, 'Yeni Sohbet', source);
        setSessions([newSession]);
        setCurrentSession(newSession);
        setMessages([]);
      }
    } catch (error) {
      console.log('Sessions load failed, continuing...', error);
      // Don't show error toast - just continue
      // Create a default session
      try {
        const newSession = await chatAPI.createSession(userId, 'Yeni Sohbet', source);
        setSessions([newSession]);
        setCurrentSession(newSession);
        setMessages([]);
      } catch (createError) {
        console.log('Could not create session, will create on first message');
      }
    }
  }, [currentSession]);
  
  /**
   * Load messages for a session
   */
  const loadMessages = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const data = await chatAPI.getMessages(sessionId);
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      showToast.error('Mesajlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);
  
  /**
   * Create new chat session
   * @param {string} userId - User ID
   * @param {string} title - Session title
   * @param {string} source - Source ("chat" or "study")
   */
  const createSession = useCallback(async (userId, title = 'Yeni Sohbet', source = 'chat') => {
    try {
      const newSession = await chatAPI.createSession(userId, title, source);
      setSessions(prev => [newSession, ...prev]);
      setCurrentSession(newSession);
      setMessages([]);
      showToast.success('Yeni Sohbet', 'Sohbet başarıyla oluşturuldu!');
      return newSession;
    } catch (error) {
      console.error('Error creating session:', error);
      showToast.error('Sohbet oluşturulamadı');
      throw error;
    }
  }, []);
  
  /**
   * Delete chat session
   */
  const deleteSession = useCallback(async (sessionId) => {
    try {
      await chatAPI.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      
      if (currentSession?.id === sessionId) {
        const remaining = sessions.filter(s => s.id !== sessionId);
        if (remaining.length > 0) {
          setCurrentSession(remaining[0]);
          await loadMessages(remaining[0].id);
        } else {
          setCurrentSession(null);
          setMessages([]);
        }
      }
      
      showToast.deleted('Sohbet Silindi', 'Sohbet geçmişinizden kaldırıldı');
    } catch (error) {
      console.error('Error deleting session:', error);
      showToast.error('Sohbet silinemedi');
      throw error;
    }
  }, [currentSession, sessions, loadMessages]);
  
  /**
   * Rename chat session
   */
  const renameSession = useCallback(async (sessionId, title) => {
    try {
      await chatAPI.renameSession(sessionId, title);
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? { ...s, title } : s
      ));
      if (currentSession?.id === sessionId) {
        setCurrentSession(prev => ({ ...prev, title }));
      }
    } catch (error) {
      console.error('Error renaming session:', error);
      showToast.error('Sohbet adı değiştirilemedi');
      throw error;
    }
  }, [currentSession]);
  
  /**
   * Load user's favorite messages
   */
  const loadFavorites = useCallback(async (userId) => {
    try {
      const data = await chatAPI.getFavorites(userId);
      setFavorites(data);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }, []);
  
  /**
   * Toggle favorite status of a message
   */
  const toggleFavorite = useCallback(async (message, userId) => {
    try {
      const isFavorited = favorites.some(f => f.message_id === message.id);
      
      if (isFavorited) {
        const fav = favorites.find(f => f.message_id === message.id);
        await chatAPI.removeFavorite(fav.id);
        setFavorites(prev => prev.filter(f => f.message_id !== message.id));
        showToast.success('Favorilerden kaldırıldı');
      } else {
        const newFav = await chatAPI.addFavorite(
          userId,
          message.id,
          message.content,
          currentSession.id
        );
        setFavorites(prev => [newFav, ...prev]);
        showToast.success('Favorilere eklendi!');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      showToast.error('İşlem başarısız');
      throw error;
    }
  }, [favorites, currentSession]);
  
  // Memoize context value
  const value = useMemo(() => ({
    sessions,
    currentSession,
    messages,
    loading,
    isTyping,
    favorites,
    setSessions,
    setCurrentSession,
    setMessages,
    setIsTyping,
    loadSessions,
    loadMessages,
    createSession,
    deleteSession,
    renameSession,
    loadFavorites,
    toggleFavorite
  }), [
    sessions,
    currentSession,
    messages,
    loading,
    isTyping,
    favorites,
    loadSessions,
    loadMessages,
    createSession,
    deleteSession,
    renameSession,
    loadFavorites,
    toggleFavorite
  ]);
  
  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
}

/**
 * useChat hook
 * Access chat context in components
 * @throws {Error} If used outside ChatProvider
 */
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within ChatProvider');
  }
  return context;
};
